import { Button } from '@neo4j-ndl/react';
import { TrialButton } from '@nx/plg';
import {
  consoleApi,
  selectConfiguration,
  useAppSelector,
  useInternalConfiguration,
  useToolsConfiguration,
} from '@nx/state';
import { AuraLogo, useCurrentScope } from '@nx/ui';
import { Header } from '@nx/upx-layout';
import { skipToken } from '@reduxjs/toolkit/query';
import { matchPath, useLocation } from 'react-router';

import { ChangelogButton } from './changelog-button';
import { HelpAndLearnButton } from './help-and-learn-button';
import { PendingInviteButton } from './invite-button';
import { OrgProjectNav } from './org-project-nav';
import { OtherPagesNav } from './other-pages-nav';
import { ProfileButton } from './profile-button';

const useShouldDisplayOrgNav = () => {
  const location = useLocation();
  const isOrgPage = matchPath('/org/:orgId/*', location.pathname) !== null;
  const isProjectPage = matchPath('/projects/:projectId/*', location.pathname) !== null;
  const toolsConfiguration = useToolsConfiguration();
  const isGuidesPage = toolsConfiguration.guides
    ? matchPath(`${toolsConfiguration.guides.route}/*`, location.pathname) !== null
    : false;
  const internalConfiguration = useInternalConfiguration();

  if (isGuidesPage) {
    return true;
  }
  return location.pathname.startsWith(internalConfiguration.basePath) || isOrgPage || isProjectPage;
};

export function ConsoleHeader({ logoRoute }: { logoRoute: string }) {
  const currentAppScope = useCurrentScope();
  const displayOrgNav = useShouldDisplayOrgNav();
  const { data: user } = consoleApi.useGetUserDetailsQuery();
  const { data: invites = [] } = consoleApi.useGetUserProjectInvitesQuery(user?.id ?? skipToken, {
    pollingInterval: 60000,
    refetchOnFocus: true,
  });
  const { pendingInvitesShown } = useAppSelector(selectConfiguration);

  return (
    <Header
      logoRoute={logoRoute}
      logoImg={<AuraLogo />}
      left={displayOrgNav ? <OrgProjectNav /> : <OtherPagesNav />}
      right={
        <>
          <TrialButton />
          <Button
            as="a"
            size="small"
            fill="outlined"
            color="neutral"
            className="text-nowrap"
            htmlAttributes={{
              target: '_blank',
              href: 'https://aura.feedback.neo4j.com/',
            }}
          >
            Feedback
          </Button>
          {invites.length > 0 && <PendingInviteButton showStatusIndicator={!pendingInvitesShown} />}
          <ChangelogButton />
          {currentAppScope !== null && <HelpAndLearnButton scope={currentAppScope} />}
          <div className="w-token-2" />
          <ProfileButton />
        </>
      }
    />
  );
}
